import React, { useState, useEffect, useCallback, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Handle, Position } from '@xyflow/react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Group, Network, ShieldCheck, ShieldX, Shield, AlertTriangle, Cloud, Database, Server, Globe, Box, Layers, HardDrive, ArrowLeftRight, User, Users } from 'lucide-react';
import awsLogoDark from '../../assets/icons/aws-dark.svg';
import awsLogoLight from '../../assets/icons/aws.svg';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ReactComponent as ClusterIcon } from '../../assets/icons/cluster.svg';
import { ReactComponent as CronJobIcon } from '../../assets/icons/cronjob.svg';
import { ReactComponent as DaemonSetIcon } from '../../assets/icons/daemonset.svg';
import { ReactComponent as DeploymentIcon } from '../../assets/icons/deployment.svg';
import { ReactComponent as JobIcon } from '../../assets/icons/job.svg';
import { ReactComponent as NamespaceIcon } from '../../assets/icons/namespace.svg';
import { ReactComponent as NodeIcon } from '../../assets/icons/node.svg';
import { ReactComponent as PodIcon } from '../../assets/icons/pod.svg';
import { ReactComponent as ReplicaSetIcon } from '../../assets/icons/replicaset.svg';
import { ReactComponent as StatefulSetIcon } from '../../assets/icons/statefulset.svg';
import { ReactComponent as ServiceIcon } from '../../assets/icons/service.svg';
import { ReactComponent as ApplicationIcon } from '../../assets/icons/application.svg';
import { apiClient, TopologyRiskFinding, TopologyIncident } from '../../api/client';
import { getStatusLabel } from '../../lib/incidents';
import { CustomNodeData, getAwsResourceTypeName, getOciResourceTypeName } from './types';
import { ChevronRight, ChevronDown } from 'lucide-react';

// --- EXPANSION INDICATOR COMPONENT ---
export const ExpansionIndicator: React.FC<{
    isExpanded?: boolean;
    isLoading?: boolean;
    childCount?: number;
    itemType?: string;
}> = memo(({ isExpanded, isLoading, childCount, itemType = 'items' }) => {
    if (isLoading) {
        return (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Loading...</span>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
            ) : (
                <ChevronRight className="h-3 w-3" />
            )}
            {childCount !== undefined && !isExpanded && (
                <span>{childCount} {itemType}</span>
            )}
        </div>
    );
});

// --- LABELS LIST COMPONENT ---
export const LabelsList: React.FC<{ labels: Record<string, unknown> }> = ({ labels }) => {
    const labelEntries = Object.entries(labels);

    if (!labelEntries || labelEntries.length === 0) {
        return <div className="text-xs text-muted-foreground">No labels</div>;
    }

    return (
        <div>
            <h4 className="font-medium text-xs mb-1.5">Labels</h4>
            <div className="space-y-2">
                {labelEntries.slice(0, 3).map(([key, value]) => (
                    <div key={key} className="text-xs font-mono grid grid-cols-[1fr,2fr] gap-x-2">
                         <div className="text-muted-foreground truncate" title={key}>{key}</div>
                         <div className="break-all font-medium" title={String(value)}>{String(value)}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// --- CUSTOM TOOLTIP COMPONENT ---
export const CustomTooltip: React.FC<{ content: React.ReactNode | ((closeTooltip: () => void) => React.ReactNode); children: React.ReactNode; delay?: number }> = ({ content, children, delay = 500 }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const showTimeout = useRef<NodeJS.Timeout | null>(null);
    const hideTimeout = useRef<NodeJS.Timeout | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const showTooltip = useCallback((e: React.MouseEvent) => {
        if (hideTimeout.current) clearTimeout(hideTimeout.current);

        // Calculate position relative to viewport
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setPosition({
                x: rect.left + rect.width / 2,
                y: rect.top
            });
        }

        showTimeout.current = setTimeout(() => {
            setIsVisible(true);
        }, delay);
    }, [delay]);

    const hideTooltip = useCallback(() => {
        if (showTimeout.current) clearTimeout(showTimeout.current);
        hideTimeout.current = setTimeout(() => {
            setIsVisible(false);
        }, 200);
    }, []);

    const closeTooltipImmediately = useCallback(() => {
        if (showTimeout.current) clearTimeout(showTimeout.current);
        if (hideTimeout.current) clearTimeout(hideTimeout.current);
        setIsVisible(false);
    }, []);

    useEffect(() => {
        return () => {
            if (showTimeout.current) clearTimeout(showTimeout.current);
            if (hideTimeout.current) clearTimeout(hideTimeout.current);
        };
    }, []);

    const tooltipElement = isVisible ? (
        <div
            className="fixed z-[9999] px-3 py-2.5 bg-popover text-popover-foreground border border-border rounded-md shadow-xl max-w-md min-w-[300px] pointer-events-auto"
            style={{
                left: position.x,
                top: position.y - 8,
                transform: 'translate(-50%, -100%)',
                zIndex: 9999
            }}
            onMouseEnter={() => {
                if (hideTimeout.current) clearTimeout(hideTimeout.current);
            }}
            onMouseLeave={hideTooltip}
        >
            <div className="text-sm space-y-1">{typeof content === 'function' ? content(closeTooltipImmediately) : content}</div>
        </div>
    ) : null;

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full"
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
        >
            {children}
            {tooltipElement && createPortal(tooltipElement, document.body)}
        </div>
    );
};

// --- DETAILS VIEW COMPONENT ---
export const DetailsView: React.FC<{
    data: CustomNodeData;
    onRiskCountUpdate?: (findings: TopologyRiskFinding[]) => void;
    onIncidentCountUpdate?: (incidents: TopologyIncident[]) => void;
}> = ({ data, onRiskCountUpdate, onIncidentCountUpdate }) => {
    const navigate = useNavigate();
    const [findings, setFindings] = useState<TopologyRiskFinding[]>([]);
    const [isLoadingFindings, setIsLoadingFindings] = useState(false);
    const [findingsError, setFindingsError] = useState<string | null>(null);

    const [incidents, setIncidents] = useState<TopologyIncident[]>([]);
    const [isLoadingIncidents, setIsLoadingIncidents] = useState(true);
    const [incidentsError, setIncidentsError] = useState<string | null>(null);

    const loadingStartedRef = useRef(false);

    const labels = data.labels || {};
    const labelEntries = Object.entries(labels);

    const renderDataPoint = (label: string, value: React.ReactNode) => (
        value ? <div className="grid grid-cols-1 sm:grid-cols-[320px,1fr] gap-x-4 gap-y-0.5 py-2 px-4 border-b last:border-b-0">
            <span className="font-semibold text-sm text-muted-foreground">{label}</span>
            <span className="text-sm break-all">{value}</span>
        </div> : null
    );

    // Load all data when component mounts - using ref to prevent duplicate calls
    useEffect(() => {
        if (!loadingStartedRef.current) {
            loadingStartedRef.current = true;
            loadAllData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - only run once on mount

    const loadAllData = async () => {
        await Promise.allSettled([
            /* fetchRiskFindings() — hidden while risks feature is disabled */
            fetchIncidents()
        ]);
    };

    /* Risk findings fetching — hidden while risks feature is disabled
    const fetchRiskFindings = async () => {
        setIsLoadingFindings(true);
        setFindingsError(null);

        try {
            let params: any = {};

            if (data.type === 'workload' && data.namespaceName) {
                const clusterID = data.clusterUID || '';
                params = {
                    cluster_id: clusterID,
                    resource_type: 'workload' as const,
                    namespace: data.namespaceName,
                    workload: data.label,
                    resource_kind: data.kind
                };
            } else if (data.type === 'namespace' && data.clusterUID) {
                params = {
                    cluster_id: data.clusterUID,
                    resource_type: 'namespace' as const,
                    namespace: data.label
                };
            } else if (data.type === 'service' && data.namespaceName) {
                const clusterID = data.clusterUID || '';
                params = {
                    cluster_id: clusterID,
                    resource_type: 'workload' as const,
                    namespace: data.namespaceName,
                    workload: data.label
                };
            } else if (data.type === 'cluster' && data.clusterUID) {
                params = {
                    cluster_id: data.clusterUID,
                    resource_type: 'cluster' as const
                };
            } else {
                setFindingsError('Unable to fetch risk findings for this resource type');
                setIsLoadingFindings(false);
                return;
            }

            const response = await apiClient.getRiskFindingsByResource(params);
            const fetchedFindings = response.findings || [];
            setFindings(fetchedFindings);

            if (onRiskCountUpdate) {
                onRiskCountUpdate(fetchedFindings);
            }
        } catch (error: any) {
            console.error('Error fetching risk findings:', error);
            setFindingsError('Failed to load risk findings');
        } finally {
            setIsLoadingFindings(false);
        }
    };
    */

    // Function to fetch incidents
    const fetchIncidents = async () => {
        setIsLoadingIncidents(true);
        setIncidentsError(null);

        try {
            let params: any = {};

            if (data.type === 'workload' && data.namespaceName) {
                const clusterID = data.clusterUID || '';
                params = {
                    cluster_id: clusterID,
                    resource_type: 'workload' as const,
                    namespace: data.namespaceName,
                    workload: data.label
                };
            } else if (data.type === 'namespace' && data.clusterUID) {
                params = {
                    cluster_id: data.clusterUID,
                    resource_type: 'namespace' as const,
                    namespace: data.label
                };
            } else if (data.type === 'service' && data.namespaceName) {
                const clusterID = data.clusterUID || '';
                params = {
                    cluster_id: clusterID,
                    resource_type: 'service' as const,
                    namespace: data.namespaceName,
                    service: data.label
                };
            } else if (data.type === 'cluster' && data.clusterUID) {
                params = {
                    cluster_id: data.clusterUID,
                    resource_type: 'cluster' as const
                };
            } else {
                setIncidentsError('Unable to fetch incidents for this resource type');
                setIsLoadingIncidents(false);
                return;
            }

            const response = await apiClient.getIncidentsByResource(params);
            const fetchedIncidents = response.incidents || [];
            setIncidents(fetchedIncidents);

            // Notify parent component about incident counts
            if (onIncidentCountUpdate) {
                onIncidentCountUpdate(fetchedIncidents);
            }
        } catch (error: any) {
            console.error('Error fetching incidents:', error);
            setIncidentsError('Failed to load incidents');
        } finally {
            setIsLoadingIncidents(false);
        }
    };

    const getSeverityColor = (severity: string) => {
        switch (severity) {
            case 'high': return 'text-foreground bg-muted/50 border-border';
            case 'medium': return 'text-foreground bg-muted/30 border-border';
            case 'low': return 'text-muted-foreground bg-muted/20 border-border';
            default: return 'text-gray-600 bg-gray-50 border-gray-200 dark:bg-gray-950/20 dark:border-gray-800';
        }
    };

    const getSeverityIcon = (severity: string) => {
        switch (severity) {
            case 'high': return <ShieldX className="h-4 w-4" />;
            case 'medium': return <ShieldCheck className="h-4 w-4" />;
            case 'low': return <ShieldCheck className="h-4 w-4" />;
            default: return <ShieldCheck className="h-4 w-4" />;
        }
    };

    // Get border color for incident cards based on severity
    const getBorderColor = (severity: string) => {
        switch (severity) {
            case 'critical': return 'border-l-foreground/60';
            case 'high': return 'border-l-foreground/60';
            case 'medium': return 'border-l-foreground/60';
            case 'low': return 'border-l-foreground/60';
            default: return 'border-l-gray-500';
        }
    };

    // Get severity badge color for incidents
    const getIncidentSeverityColor = (severity: string) => {
        switch (severity) {
            case 'critical': return 'bg-foreground text-background border-foreground/50';
            case 'high': return 'bg-foreground/80 text-background border-foreground/40';
            case 'medium': return 'bg-muted text-foreground border-border';
            case 'low': return 'bg-muted/50 text-muted-foreground border-border';
            default: return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-900/30 dark:text-gray-300';
        }
    };

    // Format text that contains {{code}}...{{/code}} markers into actual code elements
    const formatCodeTags = (text: string) => {
        if (!text) return null;
        const parts = text.split(/(\{{1,2}code\}{1,2}[\s\S]*?\{{1,2}\/code\}{1,2})/gi);
        return parts.map((part, index) => {
            const codeMatch = part.match(/\{{1,2}code\}{1,2}([\s\S]*?)\{{1,2}\/code\}{1,2}/i);
            if (codeMatch) {
                return (
                    <code key={index} className="px-1.5 py-0.5 bg-muted rounded text-sm font-mono">
                        {codeMatch[1]}
                    </code>
                );
            }
            return <span key={index}>{part}</span>;
        });
    };

    const detailsContent = (
        <div className="py-4">
            <div className="rounded-md border">
                {renderDataPoint("Name", data.label)}
                {data.type === 'cluster' && renderDataPoint("Description", data.description)}
                {data.type === 'cluster' && renderDataPoint("Connected", data.connectionStatus ? (data.connectionStatus === 'connected' ? 'Yes' : data.connectionStatus === 'degraded' ? 'Degraded' : 'No') : (data.isConnected ? 'Yes' : 'No'))}
                {data.type === 'node' && data.internalIps && renderDataPoint("Internal IPs", data.internalIps.join(', '))}
                {data.type === 'node' && data.externalIps && data.externalIps.length > 0 && renderDataPoint("External IPs", data.externalIps.join(', '))}
                {data.type === 'node' && renderDataPoint("Hostname", data.hostname)}
                {data.type === 'node' && data.ready !== undefined && renderDataPoint("Ready", data.ready ? 'Yes' : 'No')}
                {data.type === 'node' && renderDataPoint("Instance Type", data.instanceType)}
                {data.type === 'node' && renderDataPoint("Zone", data.zone)}
                {data.type === 'workload' && renderDataPoint("Kind", data.kind)}
                {data.type === 'service' && renderDataPoint("Service Type", data.serviceType)}
                {data.type === 'service' && renderDataPoint("Cluster IP", data.clusterIp)}
                {data.type === 'service' && data.externalIps && data.externalIps.length > 0 && renderDataPoint("External IPs", data.externalIps.join(', '))}
                {data.type === 'namespace' && renderDataPoint("Cluster UID", data.clusterUID)}
                {data.type === 'workload' && renderDataPoint("Namespace ID", data.namespaceID)}
                {renderDataPoint("Created At", data.lastSeen ? new Date(data.lastSeen).toLocaleString() : 'N/A')}
            </div>

            {labelEntries.length > 0 && (
                <div className="pt-6">
                    <h4 className="font-semibold text-lg mb-2">Labels</h4>
                    <div className="rounded-md border bg-muted/20 max-h-72 overflow-y-auto">
                        {labelEntries.map(([key, value]) => (
                             <div key={key} className="grid grid-cols-1 sm:grid-cols-[320px,1fr] gap-x-4 gap-y-0.5 py-2 px-4 border-b last:border-b-0">
                                <span className="font-semibold text-sm text-muted-foreground" title={key}>{key}</span>
                                <span className="text-sm break-all" title={String(value)}>{String(value)}</span>
                             </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const riskContent = (
        <div className="py-4">
            {isLoadingFindings ? (
                <div className="flex justify-center items-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">Loading risk findings...</span>
                </div>
            ) : findingsError ? (
                <Alert variant="destructive">
                    <AlertDescription>{findingsError}</AlertDescription>
                </Alert>
            ) : findings.length === 0 ? (
                <div className="text-center py-12">
                    
                    <h3 className="mt-2 text-sm font-medium text-foreground">No risk findings</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                        No security risks have been identified for this resource in the latest assessment.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="text-sm text-muted-foreground mb-4">
                        Found {findings.length} risk{findings.length !== 1 ? 's' : ''} affecting this resource
                    </div>
                    <div className="space-y-4">
                        {findings.map((finding) => (
                        <Card key={finding.id} className={`border-l-4 bg-background dark:bg-card ${getSeverityColor(finding.severity).split(' ')[1]}`}>
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between gap-2">
                                    <CardTitle className="text-base flex items-start gap-2 text-foreground">
                                        {getSeverityIcon(finding.severity)}
                                        <span>{finding.title}</span>
                                    </CardTitle>
                                    <Badge className={getSeverityColor(finding.severity)}>
                                        {finding.severity.toUpperCase()}
                                    </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground mt-1">
                                    Category: {finding.category}
                                </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                                <p className="text-sm text-muted-foreground mb-3">{finding.description}</p>
                                <div className="border-t dark:border-border pt-3">
                                    <h4 className="text-sm font-semibold mb-2 text-foreground">Recommendation</h4>
                                    <p className="text-sm text-muted-foreground">{finding.recommendation}</p>
                                </div>
                            </CardContent>
                        </Card>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const incidentsContent = (
        <div className="py-4">
            {isLoadingIncidents ? (
                <div className="flex justify-center items-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    <span className="ml-2 text-muted-foreground">Loading incidents...</span>
                </div>
            ) : incidentsError ? (
                <Alert variant="destructive">
                    <AlertDescription>{incidentsError}</AlertDescription>
                </Alert>
            ) : incidents.length === 0 ? (
                <div className="text-center py-12">
                    
                    <h3 className="mt-2 text-sm font-medium text-foreground">No active incidents</h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                        No incidents have been detected for this resource.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    <div className="text-sm text-muted-foreground mb-4">
                        Found {incidents.length} active incident{incidents.length !== 1 ? 's' : ''} affecting this resource
                    </div>
                    <div className="space-y-4">
                        {incidents.map((incident) => (
                        <Card key={incident.id} className={cn("border-l-4", getBorderColor(incident.severity))}>
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4 text-destructive" />
                                            <h4 className="text-sm font-semibold leading-none tracking-tight">
                                                {incident.incident_type && incident.incident_type !== 'Unknown' ? incident.incident_type : incident.type}
                                            </h4>
                                        </div>
                                        <div className="flex items-center gap-2 pt-1">
                                            <Badge className={cn("text-xs", getIncidentSeverityColor(incident.severity))}>
                                                {incident.severity.toUpperCase()}
                                            </Badge>
                                            <Badge variant="outline" className="text-xs">
                                                {getStatusLabel(incident.status)}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent className="pt-0">
                                {incident.root_cause && (
                                    <p className="text-sm text-muted-foreground mb-3">{formatCodeTags(incident.root_cause)}</p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                    Detected: {new Date(incident.detected_at * 1000).toLocaleString()}
                                </p>
                                <div className="mt-3 pt-3 border-t">
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 h-auto text-xs"
                                        onClick={() => navigate(`/incidents/${incident.id}`)}
                                    >
                                        View Full Incident Details
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    // Only show tabs for workload, namespace, cluster, and service types
    if (data.type === 'workload' || data.type === 'namespace' || data.type === 'cluster' || data.type === 'service') {
        const isLoadingAnyData = isLoadingFindings || isLoadingIncidents;

        return (
            <div className="w-full">
                {isLoadingAnyData && (
                    <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading data for all tabs...
                    </div>
                )}
                <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="incidents" className="flex items-center justify-center gap-2">
                        <span>Incidents</span>
                        {!isLoadingIncidents && incidents.length > 0 && (
                            <Badge variant="secondary" className="h-5 px-1.5">
                                {incidents.length}
                            </Badge>
                        )}
                    </TabsTrigger>
                    {/* Risks tab hidden — uncomment to re-enable
                    <TabsTrigger value="risks" className="flex items-center justify-center gap-2">
                        <span>Risks</span>
                        {!isLoadingFindings && findings.length > 0 && (
                            <Badge variant="destructive" className="h-5 px-1.5">
                                {findings.length}
                            </Badge>
                        )}
                    </TabsTrigger>
                    */}
                </TabsList>
                <TabsContent value="details" className="mt-0 max-h-[60vh] sm:h-[500px] overflow-y-auto">
                    {detailsContent}
                </TabsContent>
                <TabsContent value="incidents" className="mt-0 max-h-[60vh] sm:h-[500px] overflow-y-auto">
                    {incidentsContent}
                </TabsContent>
                {/* Risks content hidden — uncomment to re-enable
                <TabsContent value="risks" className="mt-0 max-h-[60vh] sm:h-[500px] overflow-y-auto">
                    {riskContent}
                </TabsContent>
                */}
            </Tabs>
            </div>
        );
    }

    // For other types, just show the details without tabs
    return detailsContent;
};

// --- CONTAINER NODE ---
export const ContainerNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
     const getContainerStyle = () => {
        // Check for incidents on namespace containers (expanded namespaces)
        if (data.containerType === 'namespace' && (data.incidentCount ?? 0) > 0) {
            return 'border-2 border-dashed animate-incident-border-pulse bg-transparent';
        }
        if (data.containerType === 'application') {
            return 'border-2 border-dotted border-zinc-400/80 bg-zinc-50/40 dark:border-zinc-500/70 dark:bg-zinc-950/30';
        }
        // AWS container styles
        if (data.containerType === 'aws-subnet') {
            return 'border-2 border-dashed border-foreground/15 bg-muted/10 dark:border-foreground/20 dark:bg-muted/5';
        }
        // OCI container styles
        if (data.containerType === 'oci-tenancy') {
            return 'border-2 border-dashed border-foreground/20 bg-muted/10 dark:border-foreground/25 dark:bg-muted/5';
        }
        if (data.containerType === 'oci-vcn') {
            return 'border-2 border-dashed border-foreground/15 bg-muted/10 dark:border-foreground/20 dark:bg-muted/5';
        }
        if (data.containerType === 'oci-subnet') {
            return 'border-2 border-dashed border-foreground/10 bg-muted/5 dark:border-foreground/15 dark:bg-muted/5';
        }
        if (data.containerType === 'oci-global') {
            return 'border-2 border-dashed border-gray-400/60 bg-gray-50/30 dark:border-gray-500/50 dark:bg-gray-950/20';
        }
         switch (data.level) {
             case 0: return 'border-2 border-dashed border-foreground/20 bg-background/5 dark:border-foreground/30 dark:bg-background/10';
             case 1: return 'border-2 border-dashed border-foreground/15 bg-muted/20 dark:border-foreground/25 dark:bg-muted/10';
             case 2: return 'border-2 border-dashed border-foreground/10 bg-muted/10 dark:border-foreground/20 dark:bg-muted/5';
             default: return 'border-2 border-dashed border-muted-foreground/30 bg-muted/20 dark:border-muted-foreground/40 dark:bg-muted/10';
         }
     };
     const getHeaderText = () => data.label;
     const getBadgeText = () => {
         const count = data.count || 0;
         if (data.type === 'container') {
             // Handle AWS container types
             if (data.containerType === 'aws-account') {
                 return count === 1 ? 'VPC' : 'VPCs';
             }
             if (data.containerType === 'aws-vpc') {
                 return count === 1 ? 'subnet' : 'subnets';
             }
             if (data.containerType === 'aws-subnet') {
                 return count === 1 ? 'resource' : 'resources';
             }
             if (data.containerType === 'aws-global') {
                 return count === 1 ? 'resource' : 'resources';
             }
             // Handle OCI container types
             if (data.containerType === 'oci-tenancy') {
                 return count === 1 ? 'VCN' : 'VCNs';
             }
             if (data.containerType === 'oci-vcn') {
                 return count === 1 ? 'resource' : 'resources';
             }
             if (data.containerType === 'oci-subnet') {
                 return count === 1 ? 'resource' : 'resources';
             }
             if (data.containerType === 'oci-global') {
                 return count === 1 ? 'resource' : 'resources';
             }
             // Handle K8s container types - check containerType first
             if (data.containerType === 'cluster') {
                 // Expanded cluster shows namespaces
                 return count === 1 ? 'namespace' : 'namespaces';
             }
             if (data.containerType === 'namespace') {
                 // Expanded namespace shows workloads
                 return count === 1 ? 'workload' : 'workloads';
             }
             // Fallback to level-based logic for root container
             if (data.level === 0) return count === 1 ? 'cluster' : 'clusters';
             if (data.level === 1) {
                 if (data.label === 'Nodes' || data.label.startsWith('Nodes (')) {
                     return count === 1 ? 'node' : 'nodes';
                 }
                 return count === 1 ? 'namespace' : 'namespaces';
             }
             if (data.level === 2) return count === 1 ? 'workload' : 'workloads';
         }
         return count === 1 ? 'item' : 'items';
     };
     const getContainerIcon = () => {
         if (data.containerType === 'application') return <ApplicationIcon className="h-4 w-4" />;
         // AWS container icons
         if (data.containerType === 'aws-account') return <><img src={awsLogoLight} alt="AWS" className="h-4 w-4 dark:hidden" /><img src={awsLogoDark} alt="AWS" className="h-4 w-4 hidden dark:block" /></>;
         if (data.containerType === 'aws-vpc') return <Network className="h-4 w-4 text-muted-foreground" />;
         if (data.containerType === 'aws-subnet') return <Layers className="h-4 w-4 text-muted-foreground" />;
         if (data.containerType === 'aws-global') return <Globe className="h-4 w-4 text-muted-foreground" />;
         // OCI container icons
         if (data.containerType === 'oci-tenancy') return <Cloud className="h-4 w-4 text-[#C74634]" />;
         if (data.containerType === 'oci-vcn') return <Network className="h-4 w-4 text-muted-foreground" />;
         if (data.containerType === 'oci-subnet') return <Layers className="h-4 w-4 text-muted-foreground" />;
         if (data.containerType === 'oci-global') return <Globe className="h-4 w-4 text-gray-500" />;
         return null;
     };

     // Generate tooltip content for AWS VPC and subnet containers
     const getTooltipContent = () => {
         if (data.containerType === 'aws-vpc') {
             return (
                 <div className="space-y-2">
                     <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                         <Network className="h-5 w-5 text-muted-foreground" />
                         VPC: {data.label}
                     </div>
                     <div className="space-y-1">
                         {data.vpcId && (
                             <div className="text-xs flex items-center gap-2">
                                 <span className="font-medium text-muted-foreground">VPC ID:</span>
                                 <span className="font-mono">{data.vpcId}</span>
                             </div>
                         )}
                         {data.awsRegion && (
                             <div className="text-xs">
                                 <span className="font-medium text-muted-foreground">Region:</span>
                                 <span className="ml-1">{data.awsRegion}</span>
                             </div>
                         )}
                         {data.awsAccountId && (
                             <div className="text-xs">
                                 <span className="font-medium text-muted-foreground">Account:</span>
                                 <span className="ml-1 font-mono">{data.awsAccountId}</span>
                             </div>
                         )}
                         {data.count !== undefined && (
                             <div className="text-xs">
                                 <span className="font-medium text-muted-foreground">Subnets:</span>
                                 <span className="ml-1">{data.count}</span>
                             </div>
                         )}
                     </div>
                 </div>
             );
         }
         if (data.containerType === 'aws-subnet') {
             return (
                 <div className="space-y-2">
                     <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                         <Layers className="h-5 w-5 text-muted-foreground" />
                         Subnet: {data.label}
                     </div>
                     <div className="space-y-1">
                         {data.subnetId && (
                             <div className="text-xs flex items-center gap-2">
                                 <span className="font-medium text-muted-foreground">Subnet ID:</span>
                                 <span className="font-mono">{data.subnetId}</span>
                             </div>
                         )}
                         {data.availabilityZone && (
                             <div className="text-xs">
                                 <span className="font-medium text-muted-foreground">Availability Zone:</span>
                                 <span className="ml-1">{data.availabilityZone}</span>
                             </div>
                         )}
                         {data.vpcId && (
                             <div className="text-xs">
                                 <span className="font-medium text-muted-foreground">VPC ID:</span>
                                 <span className="ml-1 font-mono">{data.vpcId}</span>
                             </div>
                         )}
                         {data.count !== undefined && (
                             <div className="text-xs">
                                 <span className="font-medium text-muted-foreground">Resources:</span>
                                 <span className="ml-1">{data.count}</span>
                             </div>
                         )}
                     </div>
                 </div>
             );
         }
         if (data.containerType === 'aws-global') {
             return (
                 <div className="space-y-2">
                     <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                         <Globe className="h-5 w-5 text-gray-500" />
                         Global Resources
                     </div>
                     <div className="space-y-1">
                         <div className="text-xs text-muted-foreground">
                             Resources not bound to a specific VPC (S3, IAM, DynamoDB, etc.)
                         </div>
                         {data.awsAccountId && (
                             <div className="text-xs">
                                 <span className="font-medium text-muted-foreground">Account:</span>
                                 <span className="ml-1 font-mono">{data.awsAccountId}</span>
                             </div>
                         )}
                         {data.count !== undefined && (
                             <div className="text-xs">
                                 <span className="font-medium text-muted-foreground">Resources:</span>
                                 <span className="ml-1">{data.count}</span>
                             </div>
                         )}
                     </div>
                 </div>
             );
         }
         return null;
     };

     const tooltipContent = getTooltipContent();
     const isAwsContainer = data.containerType === 'aws-vpc' || data.containerType === 'aws-subnet' || data.containerType === 'aws-global';

     const containerElement = (
        <div className={`rounded-lg p-3 transition-all duration-500 ${getContainerStyle()} w-full h-full flex flex-col`}>
            <div className="flex items-center justify-between mb-2 px-2 flex-shrink-0">
                <div className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 min-w-0">
                    {getContainerIcon()}
                    <span className="truncate">{getHeaderText()}</span>
                    {/* Show region for AWS VPC containers */}
                    {data.containerType === 'aws-vpc' && data.awsRegion && (
                        <span className="text-[10px] opacity-70 whitespace-nowrap flex-shrink-0">({data.awsRegion})</span>
                    )}
                    {/* Show AZ for AWS subnet containers */}
                    {data.containerType === 'aws-subnet' && data.availabilityZone && (
                        <span className="text-[10px] opacity-70 whitespace-nowrap flex-shrink-0">({data.availabilityZone})</span>
                    )}
                </div>
                {data.count !== undefined && (
                    <Badge variant="outline" className="text-xs flex-shrink-0">{data.count} {getBadgeText()}</Badge>
                )}
            </div>
            {data.count === 0 && (
                <div className="flex-grow flex items-center justify-center text-center">
                    <div className="text-muted-foreground p-4">
                        <p className="font-semibold text-base">Empty</p>
                        <p className="text-sm mt-1">
                            {data.level === 2 ? `No workloads or services found in this namespace.` : `No items to display.`}
                        </p>
                    </div>
                </div>
            )}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: -1 }} />
        </div>
     );

     // Wrap AWS containers with tooltip, others return as-is
     if (isAwsContainer && tooltipContent) {
         return (
             <CustomTooltip content={tooltipContent}>
                 {containerElement}
             </CustomTooltip>
         );
     }

     return containerElement;
});

// --- CLUSTER NODE ---
export const ClusterNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    // Use incident count from data (API response) or fall back to state updated by details view
    const [localIncidentCount, setLocalIncidentCount] = useState(0);
    const incidentCount = data.incidentCount ?? localIncidentCount;
    const displayName = data.label.length > 20 ? data.label.substring(0, 20) + '...' : data.label;

    // Determine if this is an expandable cluster in expansion mode
    const isExpandable = data.isExpandable ?? false;
    const isExpanded = data.isExpanded ?? false;
    const isLoading = data.isLoading ?? false;

    // Derive effective connection status from connectionStatus (preferred) or isConnected (fallback)
    const rawStatus = data.connectionStatus;
    const effectiveConnectionStatus: 'connected' | 'degraded' | 'disconnected' =
        (rawStatus === 'connected' || rawStatus === 'degraded' || rawStatus === 'disconnected')
            ? rawStatus
            : (data.isConnected ? 'connected' : 'disconnected');

    // Determine border style - red pulse if incidents exist
    const getBorderStyles = () => {
        if (incidentCount > 0) {
            return 'border-2 border-dashed animate-incident-border-pulse bg-transparent';
        }
        if (effectiveConnectionStatus === 'connected') {
            return 'border-foreground/30 bg-muted/30 dark:border-foreground/20 dark:bg-muted/10';
        }
        if (effectiveConnectionStatus === 'degraded') {
            return 'border-muted-foreground/50 bg-muted/30 dark:border-muted-foreground/40 dark:bg-muted/20';
        }
        return 'border-muted bg-muted/50 dark:border-muted-foreground dark:bg-muted/20';
    };

    const handleIncidentUpdate = (incidents: TopologyIncident[]) => {
        setLocalIncidentCount(incidents.length);
    };

    return (
    <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
    <CustomTooltip content={(closeTooltip) =>
        <div className="space-y-2">
            <div className="font-semibold text-foreground flex items-center gap-2 text-sm"><ClusterIcon className="h-5 w-5" />{data.label}</div>

            <div className="space-y-1">
                <div className="text-xs flex items-center gap-2">
                    <span className="font-medium text-muted-foreground">Status:</span>
                    <Badge
                        variant={effectiveConnectionStatus === 'connected' ? 'default' : effectiveConnectionStatus === 'degraded' ? 'outline' : 'destructive'}
                        className={`text-xs ${effectiveConnectionStatus === 'degraded' ? 'border-muted-foreground text-muted-foreground' : ''}`}
                    >
                        {effectiveConnectionStatus === 'connected' ? 'Connected' : effectiveConnectionStatus === 'degraded' ? 'Degraded' : 'Disconnected'}
                    </Badge>
                </div>

                {data.description && (
                    <div className="text-xs">
                        <span className="font-medium text-muted-foreground">Description:</span>
                        <div className="text-foreground/90 mt-1">{data.description}</div>
                    </div>
                )}

                {isExpandable && data.childCount !== undefined && (
                    <div className="text-xs">
                        <span className="font-medium text-muted-foreground">Namespaces:</span>
                        <span className="ml-1">{data.childCount}</span>
                    </div>
                )}
            </div>

            {incidentCount > 0 && (
                <div className="pt-2 border-t">
                    <Badge variant="destructive" className="text-xs">
                        {incidentCount} Active Incident{incidentCount !== 1 ? 's' : ''}
                    </Badge>
                </div>
            )}

            {isExpandable && (
                <div className="text-xs text-muted-foreground pt-1 border-t">
                    Click to {isExpanded ? 'collapse' : 'expand'} cluster
                </div>
            )}

            <Button variant="link" size="sm" className="p-0 h-auto text-xs w-full text-left" onClick={(e) => { e.stopPropagation(); closeTooltip(); setIsDetailsOpen(true); }}>See more details...</Button>
        </div>
    }>
        <div className="relative w-full h-full">
            <Handle type="target" position={Position.Left} style={{ top: '50%' }} />
            <Handle type="source" position={Position.Right} style={{ top: '50%' }} />
            <Card className={`p-2 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer w-full h-full ${getBorderStyles()} ${isExpandable ? 'hover:border-foreground/40 hover:bg-muted/30 dark:hover:border-foreground/30 dark:hover:bg-muted/20' : ''}`}>
                <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                    <div className="relative flex-shrink-0 h-8 w-8 flex items-center justify-center">
                        <ClusterIcon className="h-6 w-6" />
                        {isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/50 rounded">
                                <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                        )}
                    </div>
                    <div className="mt-1 w-full min-w-0">
                        <h3 className="font-medium text-foreground text-xs break-words line-clamp-2" title={data.label}>{displayName}</h3>
                    </div>
                    {isExpandable && (
                        <ExpansionIndicator
                            isExpanded={isExpanded}
                            isLoading={isLoading}
                            childCount={data.childCount}
                            itemType="namespaces"
                        />
                    )}
                </div>
            </Card>
        </div>
    </CustomTooltip>
    <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <DialogHeader className="pr-8"><DialogTitle>Cluster Details: {data.label}</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-y-auto">
            <DetailsView data={data} onIncidentCountUpdate={handleIncidentUpdate} />
        </div>
    </DialogContent>
    </Dialog>
)});

// --- NAMESPACE NODE ---
export const NamespaceNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const displayName = data.label.length > 20 ? data.label.substring(0, 20) + '...' : data.label;

    // Determine if this is an expandable namespace in expansion mode
    const isExpandable = data.isExpandable ?? false;
    const isExpanded = data.isExpanded ?? false;
    const isLoading = data.isLoading ?? false;
    const incidentCount = data.incidentCount ?? 0;

    // Determine border style - red pulse if incidents exist, default blue otherwise
    const getBorderStyles = () => {
        if (incidentCount > 0) {
            return 'border-2 border-dashed animate-incident-border-pulse bg-transparent';
        }
        if (isExpandable) {
            return 'border-foreground/30 bg-muted/20 hover:border-foreground/50 hover:bg-muted/30 dark:border-foreground/20 dark:bg-muted/10 dark:hover:border-foreground/40 dark:hover:bg-muted/20';
        }
        return 'border-foreground/30 bg-muted/20 dark:border-foreground/20 dark:bg-muted/10';
    };

    return (
    <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
    <CustomTooltip content={(closeTooltip) =>
        <div className="space-y-2">
            <div className="font-semibold text-foreground flex items-center gap-2 text-sm"><NamespaceIcon className="h-5 w-5" />{data.label}</div>
            <LabelsList labels={data.labels || {}} />

            {isExpandable && data.childCount !== undefined && (
                <div className="text-xs">
                    <span className="font-medium text-muted-foreground">Workloads:</span>
                    <span className="ml-1">{data.childCount}</span>
                </div>
            )}

            {incidentCount > 0 && (
                <div className="pt-2 border-t">
                    <Badge variant="destructive" className="text-xs">
                        {incidentCount} Active Incident{incidentCount !== 1 ? 's' : ''}
                    </Badge>
                </div>
            )}

            {isExpandable && (
                <div className="text-xs text-muted-foreground pt-1 border-t">
                    Click to {isExpanded ? 'collapse' : 'expand'} namespace
                </div>
            )}

            <Button variant="link" size="sm" className="p-0 h-auto text-xs w-full text-left" onClick={(e) => { e.stopPropagation(); closeTooltip(); setIsDetailsOpen(true); }}>See more details...</Button>
        </div>
    }>
        <div className="relative w-full h-full">
            <Handle type="target" position={Position.Left} style={{ top: '50%' }} />
            <Handle type="source" position={Position.Right} style={{ top: '50%' }} />
            <Card className={`p-2 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer w-full h-full ${getBorderStyles()}`}>
                <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                    <div className="relative flex-shrink-0">
                        <NamespaceIcon className="h-6 w-6" />
                        {isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/50 rounded">
                                <Loader2 className="h-4 w-4 animate-spin" />
                            </div>
                        )}
                    </div>
                    <h4 className="font-medium text-foreground text-xs break-words line-clamp-2 mt-1 w-full min-w-0" title={data.label}>{displayName}</h4>
                    {isExpandable && (
                        <ExpansionIndicator
                            isExpanded={isExpanded}
                            isLoading={isLoading}
                            childCount={data.childCount}
                            itemType="workloads"
                        />
                    )}
                </div>
            </Card>
        </div>
    </CustomTooltip>
    <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <DialogHeader className="pr-8"><DialogTitle>Namespace Details: {data.label}</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-y-auto">
            <DetailsView data={data} />
        </div>
    </DialogContent>
    </Dialog>
)});

// --- K8S NODE NODE ---
export const NodeNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const displayName = data.label.length > 20 ? data.label.substring(0, 20) + '...' : data.label;

    return (
    <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
    <CustomTooltip content={(closeTooltip) =>
        <div className="space-y-2">
            <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                <NodeIcon className="h-5 w-5" />
                {data.label}
            </div>
            <div className="space-y-1">
                {data.internalIps && data.internalIps.length > 0 && (
                    <div className="text-xs">
                        <span className="font-medium text-muted-foreground">Internal IPs:</span>
                        <div className="font-mono text-foreground/90 mt-1">{data.internalIps.join(', ')}</div>
                    </div>
                )}
                {data.ready !== undefined && (
                    <div className="text-xs flex items-center gap-2">
                        <span className="font-medium text-muted-foreground">Status:</span>
                        <Badge variant={data.ready ? 'default' : 'destructive'} className="text-xs">
                            {data.ready ? 'Ready' : 'Not Ready'}
                        </Badge>
                    </div>
                )}
                {data.instanceType && (
                    <div className="text-xs">
                        <span className="font-medium text-muted-foreground">Instance Type:</span>
                        <span className="text-foreground/90 ml-1">{data.instanceType}</span>
                    </div>
                )}
                {data.zone && (
                    <div className="text-xs">
                        <span className="font-medium text-muted-foreground">Zone:</span>
                        <span className="text-foreground/90 ml-1">{data.zone}</span>
                    </div>
                )}
            </div>
            <Button variant="link" size="sm" className="p-0 h-auto text-xs w-full text-left" onClick={(e) => { e.stopPropagation(); closeTooltip(); setIsDetailsOpen(true); }}>See more details...</Button>
        </div>
    }>
        <div className="relative w-full h-full">
            <Handle type="target" position={Position.Left} style={{ top: '50%' }} />
            <Handle type="source" position={Position.Right} style={{ top: '50%' }} />
            <Card className={`p-2 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer w-full h-full ${data.ready ? 'border-foreground/30 bg-muted/20 dark:border-foreground/20 dark:bg-muted/10' : 'border-destructive/50 bg-destructive/5 dark:border-destructive/40 dark:bg-destructive/5'}`}>
                <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                    <NodeIcon className="h-6 w-6 flex-shrink-0" />
                    <h4 className="font-medium text-foreground text-xs break-words line-clamp-2 mt-1 w-full min-w-0" title={data.label}>{displayName}</h4>
                </div>
            </Card>
        </div>
    </CustomTooltip>
    <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <DialogHeader className="pr-8"><DialogTitle>Node Details: {data.label}</DialogTitle></DialogHeader>
        <div className="flex-1 overflow-y-auto">
            <DetailsView data={data} />
        </div>
    </DialogContent>
    </Dialog>
)});

// --- WORKLOAD ICON HELPER ---
export const getWorkloadIcon = (kind: string = '', className: string = '') => {
    const lowerKind = kind.toLowerCase();
    if (lowerKind.includes('deployment')) return <DeploymentIcon className={className} />;
    if (lowerKind.includes('daemonset')) return <DaemonSetIcon className={className} />;
    if (lowerKind.includes('statefulset')) return <StatefulSetIcon className={className} />;
    if (lowerKind.includes('replicaset')) return <ReplicaSetIcon className={className} />;
    if (lowerKind.includes('cronjob')) return <CronJobIcon className={className} />;
    if (lowerKind.includes('job')) return <JobIcon className={className} />;
    if (lowerKind.includes('pod')) return <PodIcon className={className} />;
    return <PodIcon className={className} />;
};

// --- WORKLOAD NODE ---
export const WorkloadNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    // Use incident count from data (API response) or fall back to state updated by details view
    const [localIncidentCount, setLocalIncidentCount] = useState(0);
    const incidentCount = data.incidentCount ?? localIncidentCount;
    const displayName = data.label.length > 20 ? data.label.substring(0, 20) + '...' : data.label;

    // Pre-fetch incident count on mount so workloads highlight without opening the dialog
    useEffect(() => {
        if (data.incidentCount && data.incidentCount > 0) return;
        if (!data.namespaceName || !data.clusterUID) return;
        const params: any = {
            cluster_id: data.clusterUID,
            resource_type: 'workload',
            namespace: data.namespaceName,
            workload: data.label,
            resource_kind: data.kind
        };
        apiClient.getIncidentsByResource(params).then(res => {
            const count = (res.incidents || []).length;
            if (count > 0) setLocalIncidentCount(count);
        }).catch(() => {});
    }, [data.label, data.namespaceName, data.clusterUID, data.kind, data.incidentCount]);

    // Determine border color - red if incidents exist, default otherwise
    const getBorderStyles = () => {
        if (incidentCount > 0) {
            return 'border-2 border-dashed animate-incident-border-pulse bg-transparent';
        }
        return 'border-zinc-500 bg-zinc-50 dark:border-zinc-400 dark:bg-zinc-950/20';
    };

    const handleIncidentUpdate = (incidents: TopologyIncident[]) => {
        setLocalIncidentCount(incidents.length);
    };

    return (
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <CustomTooltip content={(closeTooltip) =>
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm">{getWorkloadIcon(data.kind, "h-5 w-5")}{data.label}</div>
                <div className="text-xs"><span className="font-medium text-muted-foreground">Kind:</span> {data.kind || 'Workload'}</div>
                <LabelsList labels={data.labels || {}} />
                {incidentCount > 0 && (
                    <div className="pt-2 border-t">
                        <Badge variant="destructive" className="text-xs">
                            {incidentCount} Active Incident{incidentCount !== 1 ? 's' : ''}
                        </Badge>
                    </div>
                )}
                <Button variant="link" size="sm" className="p-0 h-auto text-xs w-full text-left" onClick={(e) => { e.stopPropagation(); closeTooltip(); setIsDetailsOpen(true); }}>See more details...</Button>
            </div>
        }>
            <div className="relative">
                <Handle type="target" position={Position.Left} />
                <Handle type="source" position={Position.Right} />
                <Card className={`p-1 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer w-full h-full ${getBorderStyles()}`}>
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <div className="h-8 w-8 flex items-center justify-center flex-shrink-0">{getWorkloadIcon(data.kind, "h-6 w-6")}</div>
                        <div className="leading-tight mt-1 w-full px-1">
                            <h5 className="font-semibold text-xs text-foreground line-clamp-2" title={data.label}>{displayName}</h5>
                            <p className="text-[10px] text-muted-foreground truncate w-full">{data.kind}</p>
                        </div>
                    </div>
                </Card>
            </div>
        </CustomTooltip>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <DialogHeader className="pr-8"><DialogTitle>Workload Details: {data.label}</DialogTitle></DialogHeader>
            <div className="flex-1 overflow-y-auto">
                <DetailsView data={data} onIncidentCountUpdate={handleIncidentUpdate} />
            </div>
        </DialogContent>
        </Dialog>
    );
});

// --- SERVICE NODE ---
export const ServiceNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    // Use incident count from data (API response) or fall back to state updated by details view
    const [localIncidentCount, setLocalIncidentCount] = useState(0);
    const incidentCount = data.incidentCount ?? localIncidentCount;
    const displayName = data.label.length > 20 ? data.label.substring(0, 20) + '...' : data.label;

    // Pre-fetch incident count on mount so services highlight without opening the dialog
    useEffect(() => {
        if (data.incidentCount && data.incidentCount > 0) return;
        if (!data.namespaceName || !data.clusterUID) return;
        const params: any = {
            cluster_id: data.clusterUID,
            resource_type: 'workload',
            namespace: data.namespaceName,
            workload: data.label
        };
        apiClient.getIncidentsByResource(params).then(res => {
            const count = (res.incidents || []).length;
            if (count > 0) setLocalIncidentCount(count);
        }).catch(() => {});
    }, [data.label, data.namespaceName, data.clusterUID, data.incidentCount]);

    // Determine border color - red if incidents exist, default cyan otherwise
    const getBorderStyles = () => {
        if (incidentCount > 0) {
            return 'border-2 border-dashed animate-incident-border-pulse bg-transparent';
        }
        return 'border-foreground/25 bg-muted/15 dark:border-foreground/20 dark:bg-muted/10';
    };

    const handleIncidentUpdate = (incidents: TopologyIncident[]) => {
        setLocalIncidentCount(incidents.length);
    };

    return (
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <CustomTooltip content={(closeTooltip) =>
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm"><ServiceIcon className="h-5 w-5" />{data.label}</div>
                <div className="space-y-1">
                    <div className="text-xs"><span className="font-medium text-muted-foreground">Type:</span> {data.serviceType || 'Service'}</div>
                    {data.clusterIp && <div className="text-xs"><span className="font-medium text-muted-foreground">Cluster IP:</span> {data.clusterIp}</div>}
                    {data.externalIps && data.externalIps.length > 0 && (
                        <div className="text-xs"><span className="font-medium text-muted-foreground">External IPs:</span> {data.externalIps.join(', ')}</div>
                    )}
                </div>
                <LabelsList labels={data.labels || {}} />
                {incidentCount > 0 && (
                    <div className="pt-2 border-t">
                        <Badge variant="destructive" className="text-xs">
                            {incidentCount} Active Incident{incidentCount !== 1 ? 's' : ''}
                        </Badge>
                    </div>
                )}
                <Button variant="link" size="sm" className="p-0 h-auto text-xs w-full text-left" onClick={(e) => { e.stopPropagation(); closeTooltip(); setIsDetailsOpen(true); }}>See more details...</Button>
            </div>
        }>
            <div className="relative">
                <Handle type="target" position={Position.Left} />
                <Handle type="source" position={Position.Right} />
                <Card className={`p-1 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer w-full h-full ${getBorderStyles()}`}>
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <div className="h-8 w-8 flex items-center justify-center flex-shrink-0"><ServiceIcon className="h-6 w-6" /></div>
                        <div className="leading-tight mt-1 w-full px-1">
                            <h5 className="font-semibold text-xs text-foreground line-clamp-2" title={data.label}>{displayName}</h5>
                            <p className="text-[10px] text-muted-foreground truncate w-full">{data.serviceType}</p>
                        </div>
                    </div>
                </Card>
            </div>
        </CustomTooltip>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <DialogHeader className="pr-8"><DialogTitle>Service Details: {data.label}</DialogTitle></DialogHeader>
            <div className="flex-1 overflow-y-auto">
                <DetailsView data={data} onIncidentCountUpdate={handleIncidentUpdate} />
            </div>
        </DialogContent>
        </Dialog>
    );
});

// --- SUMMARY NODE ---
export const SummaryNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => (
    <CustomTooltip content={
        <div className="space-y-2">
            <div className="font-semibold text-foreground flex items-center gap-2 text-sm"><Group className="h-5 w-5" />Group: "{data.groupKey}"</div>
            <div className="space-y-1">
                <div className="text-xs font-medium text-muted-foreground">Contains {data.originalNodes?.length || 0} items:</div>
                <ul className="list-disc list-inside pl-2 text-xs space-y-0.5">
                    {data.originalNodes?.slice(0, 5).map((node: any) => (<li key={node.id}>{node.name}</li>))}
                    {data.originalNodes && data.originalNodes.length > 5 && (<li>...and {data.originalNodes.length - 5} more.</li>)}
                </ul>
            </div>
        </div>
    }>
        <Card className="p-2 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer w-full h-full border-foreground/20 bg-muted/10 dark:border-foreground/15 dark:bg-muted/5">
            <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                <Group className="h-3.5 w-3.5 text-muted-foreground" />
                <h5 className="font-medium text-xs text-foreground break-words mt-1" title={`Group: ${data.groupKey}`}>Group: {data.groupKey}...</h5>
                <Badge variant="outline" className="text-xs mt-0.5 px-1 py-0">{data.count} items</Badge>
            </div>
        </Card>
    </CustomTooltip>
));

// --- EXTERNAL NAMESPACE NODE ---
export const ExternalNamespaceNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const displayName = (data.externalNamespace || data.label).length > 20
        ? (data.externalNamespace || data.label).substring(0, 20) + '...'
        : (data.externalNamespace || data.label);

    return (
        <CustomTooltip content={
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                    <NamespaceIcon className="h-5 w-5" />
                    External Namespace: {data.externalNamespace}
                </div>
                <div className="text-xs text-muted-foreground">
                    This namespace is outside the currently viewed namespace but has network flows connected to workloads within.
                </div>
            </div>
        }>
            <div className="relative">
                <Handle type="target" position={Position.Left} />
                <Handle type="source" position={Position.Right} />
                <Card className="p-2 border-2 border-dashed transition-all duration-300 hover:shadow-lg w-full h-full border-muted-foreground/40 bg-muted/10 dark:border-muted-foreground/30 dark:bg-muted/5">
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <NamespaceIcon className="h-5 w-5 flex-shrink-0 opacity-70" />
                        <h4 className="font-medium text-xs text-foreground break-words line-clamp-2 mt-1 w-full min-w-0" title={data.externalNamespace}>
                            {displayName}
                        </h4>
                        <Badge variant="outline" className="text-xs mt-0.5 px-1 py-0">X-Namespace</Badge>
                    </div>
                </Card>
            </div>
        </CustomTooltip>
    );
});

// --- EXTERNAL IP NODE ---
export const ExternalIpNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const isAggregate = data.externalIp === 'aggregate';
    const ipCount = data.count || 1;
    const displayName = data.label.length > 20 ? data.label.substring(0, 17) + '...' : data.label;

    return (
        <CustomTooltip content={
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                    {isAggregate ? `Aggregated External Traffic` : `External IP: ${data.externalIp}`}
                </div>
                <div className="text-xs text-muted-foreground">
                    {isAggregate
                        ? `${ipCount} external sources (IPs, managed services, etc.) have network flows to/from your resources. Click on a flow line to see details.`
                        : 'This external IP address has network flows connected to your resources.'
                    }
                </div>
            </div>
        }>
            <div className="relative">
                <Handle type="target" position={Position.Left} />
                <Handle type="source" position={Position.Right} />
                <Card className="p-2 border-2 border-dashed transition-all duration-300 hover:shadow-lg w-full h-full border-muted-foreground/30 bg-muted/5 dark:border-muted-foreground/20 dark:bg-muted/5">
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <div className="h-5 w-5 rounded-full bg-muted-foreground dark:bg-muted-foreground flex-shrink-0 opacity-50" />
                        <h4 className="font-medium text-xs text-foreground break-words line-clamp-2 mt-1 w-full min-w-0" title={data.label}>
                            {displayName}
                        </h4>
                        <Badge variant="outline" className="text-xs mt-0.5 px-1 py-0">
                            {isAggregate ? `${ipCount} sources` : 'External IP'}
                        </Badge>
                    </div>
                </Card>
            </div>
        </CustomTooltip>
    );
});

// --- KNOWN IP NODE ---
// Shows external IPs that have been given friendly names by users
export const KnownIpNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const displayName = data.label.length > 20 ? data.label.substring(0, 17) + '...' : data.label;
    const ipAddress = data.externalIp || '';

    return (
        <CustomTooltip content={
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                    {data.label}
                </div>
                <div className="text-xs text-muted-foreground">
                    IP Address: {ipAddress}
                </div>
                <div className="text-xs text-muted-foreground">
                    This is a known external IP that has been named by your team.
                </div>
            </div>
        }>
            <div className="relative">
                <Handle type="target" position={Position.Left} />
                <Handle type="source" position={Position.Right} />
                <Card className="p-2 border-2 border-solid transition-all duration-300 hover:shadow-lg w-full h-full border-zinc-500 bg-zinc-50 dark:border-zinc-400 dark:bg-zinc-950/20">
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <Server className="h-5 w-5 text-zinc-500 dark:text-zinc-400 flex-shrink-0" />
                        <h4 className="font-medium text-xs text-foreground break-words line-clamp-2 mt-1 w-full min-w-0" title={data.label}>
                            {displayName}
                        </h4>
                        <Badge variant="outline" className="text-xs mt-0.5 px-1 py-0 border-zinc-300 text-zinc-600 dark:border-zinc-600 dark:text-zinc-400">
                            Known IP
                        </Badge>
                    </div>
                </Card>
            </div>
        </CustomTooltip>
    );
});

// ============================================================================
// AWS CLOUD RESOURCE NODES
// ============================================================================

// --- AWS ACCOUNT NODE ---
export const AwsAccountNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const displayName = data.label.length > 25 ? data.label.substring(0, 22) + '...' : data.label;
    const isExpandable = data.isExpandable ?? true;
    const isExpanded = data.isExpanded ?? false;
    const isLoading = data.isLoading ?? false;

    return (
        <CustomTooltip content={
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                    <><img src={awsLogoLight} alt="AWS" className="h-5 w-5 dark:hidden" /><img src={awsLogoDark} alt="AWS" className="h-5 w-5 hidden dark:block" /></>
                    AWS Account: {data.awsAccountId}
                </div>
                <div className="space-y-1">
                    <div className="text-xs flex items-center gap-2">
                        <span className="font-medium text-muted-foreground">Account ID:</span>
                        <span className="font-mono">{data.awsAccountId}</span>
                    </div>
                    {data.vpcCount !== undefined && (
                        <div className="text-xs">
                            <span className="font-medium text-muted-foreground">VPCs:</span>
                            <span className="ml-1">{data.vpcCount}</span>
                        </div>
                    )}
                    {data.resourceCount !== undefined && (
                        <div className="text-xs">
                            <span className="font-medium text-muted-foreground">Resources:</span>
                            <span className="ml-1">{data.resourceCount}</span>
                        </div>
                    )}
                </div>
                {isExpandable && (
                    <div className="text-xs text-muted-foreground pt-1 border-t">
                        Click to {isExpanded ? 'collapse' : 'expand'} account
                    </div>
                )}
            </div>
        }>
            <div className="relative w-full h-full">
                <Handle type="target" position={Position.Left} style={{ top: '50%' }} />
                <Handle type="source" position={Position.Right} style={{ top: '50%' }} />
                <Card className={`p-2 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer w-full h-full border-muted-foreground/40 bg-muted/10 dark:border-muted-foreground/30 dark:bg-muted/5 ${isExpandable ? 'hover:border-foreground/40 hover:bg-muted/30 dark:hover:border-foreground/30 dark:hover:bg-muted/20' : ''}`}>
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <div className="relative flex-shrink-0 h-8 w-8 flex items-center justify-center">
                            <><img src={awsLogoLight} alt="AWS" className="h-6 w-6 dark:hidden" /><img src={awsLogoDark} alt="AWS" className="h-6 w-6 hidden dark:block" /></>
                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/50 rounded">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                </div>
                            )}
                        </div>
                        <div className="mt-1 w-full min-w-0">
                            <h3 className="font-medium text-foreground text-xs break-words line-clamp-2" title={data.label}>{displayName}</h3>
                            <div className="text-[10px] text-muted-foreground font-mono">{data.awsAccountId}</div>
                        </div>
                        {isExpandable && (
                            <ExpansionIndicator
                                isExpanded={isExpanded}
                                isLoading={isLoading}
                                childCount={data.vpcCount}
                                itemType="VPCs"
                            />
                        )}
                    </div>
                </Card>
            </div>
        </CustomTooltip>
    );
});

// --- AWS VPC NODE ---
export const AwsVpcNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const displayName = data.label.length > 20 ? data.label.substring(0, 17) + '...' : data.label;
    const isExpandable = data.isExpandable ?? true;
    const isExpanded = data.isExpanded ?? false;
    const isLoading = data.isLoading ?? false;
    const isGlobal = data.isGlobalResource ?? false;

    const borderColor = isGlobal ? 'border-muted-foreground/40 dark:border-muted-foreground/30' : 'border-foreground/30 dark:border-foreground/20';
    const bgColor = isGlobal ? 'bg-muted/20 dark:bg-muted/10' : 'bg-muted/20 dark:bg-muted/10';
    const iconColor = isGlobal ? 'text-muted-foreground' : 'text-muted-foreground';

    return (
        <CustomTooltip content={
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                    {isGlobal ? (
                        <Globe className="h-5 w-5 text-gray-500" />
                    ) : (
                        <Network className="h-5 w-5 text-muted-foreground" />
                    )}
                    {isGlobal ? 'Global Resources' : `VPC: ${data.vpcId}`}
                </div>
                <div className="space-y-1">
                    {!isGlobal && (
                        <>
                            <div className="text-xs flex items-center gap-2">
                                <span className="font-medium text-muted-foreground">VPC ID:</span>
                                <span className="font-mono">{data.vpcId}</span>
                            </div>
                            <div className="text-xs">
                                <span className="font-medium text-muted-foreground">Region:</span>
                                <span className="ml-1">{data.awsRegion}</span>
                            </div>
                        </>
                    )}
                    {data.resourceCount !== undefined && (
                        <div className="text-xs">
                            <span className="font-medium text-muted-foreground">Resources:</span>
                            <span className="ml-1">{data.resourceCount}</span>
                        </div>
                    )}
                </div>
                {isExpandable && (
                    <div className={`text-xs pt-1 border-t ${isGlobal ? 'text-gray-600 dark:text-gray-400' : 'text-muted-foreground'}`}>
                        Click to {isExpanded ? 'collapse' : 'expand'}
                    </div>
                )}
            </div>
        }>
            <div className="relative w-full h-full">
                <Handle type="target" position={Position.Left} style={{ top: '50%' }} />
                <Handle type="source" position={Position.Right} style={{ top: '50%' }} />
                <Card className={`p-2 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer w-full h-full ${borderColor} ${bgColor}`}>
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <div className="relative flex-shrink-0 h-7 w-7 flex items-center justify-center">
                            {isGlobal ? (
                                <Globe className={`h-5 w-5 ${iconColor}`} />
                            ) : (
                                <Network className={`h-5 w-5 ${iconColor}`} />
                            )}
                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/50 rounded">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                </div>
                            )}
                        </div>
                        <div className="mt-1 w-full min-w-0">
                            <h3 className="font-medium text-foreground text-xs break-words line-clamp-2" title={data.label}>{displayName}</h3>
                            {!isGlobal && data.awsRegion && (
                                <div className="text-[10px] text-muted-foreground">{data.awsRegion}</div>
                            )}
                        </div>
                        {isExpandable && (
                            <ExpansionIndicator
                                isExpanded={isExpanded}
                                isLoading={isLoading}
                                childCount={data.resourceCount}
                                itemType="resources"
                            />
                        )}
                    </div>
                </Card>
            </div>
        </CustomTooltip>
    );
});

// --- AWS GLOBAL GROUP NODE (for IAM, S3, DynamoDB) ---
export const AwsGlobalGroupNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    // Re-use AwsVpcNode with isGlobalResource flag
    return <AwsVpcNode data={{ ...data, isGlobalResource: true }} />;
});

// Helper to get icon for resource type
const getResourceIcon = (resourceType: string): React.ReactNode => {
    const iconClass = "h-4 w-4 text-muted-foreground";
    switch (resourceType) {
        case 'AWS::EC2::Instance':
            return <Server className={iconClass} />;
        case 'AWS::EC2::SecurityGroup':
            return <Shield className={iconClass} />;
        case 'AWS::EC2::VPC':
        case 'AWS::EC2::Subnet':
            return <Network className={iconClass} />;
        case 'AWS::EC2::NatGateway':
            return <Globe className={iconClass} />;
        case 'AWS::EC2::VPCEndpoint':
            return <ArrowLeftRight className={iconClass} />;
        case 'AWS::ElasticLoadBalancingV2::LoadBalancer':
            return <Network className={iconClass} />;
        case 'AWS::RDS::DBInstance':
        case 'AWS::DynamoDB::Table':
            return <Database className={iconClass} />;
        case 'AWS::Lambda::Function':
            return <Box className={iconClass} />;
        case 'AWS::EKS::Cluster':
        case 'AWS::ECS::Cluster':
            return <Layers className={iconClass} />;
        case 'AWS::S3::Bucket':
            return <HardDrive className={iconClass} />;
        default:
            return <Cloud className={iconClass} />;
    }
};

// --- AWS RESOURCE NODE ---
export const AwsResourceNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const displayName = data.label.length > 18 ? data.label.substring(0, 15) + '...' : data.label;
    const resourceTypeName = getAwsResourceTypeName(data.resourceType || '');
    const incidentCount = data.incidentCount ?? 0;

    // Determine border style - red pulse if incidents exist, default orange otherwise
    const getBorderStyles = () => {
        if (incidentCount > 0) {
            return 'border-2 border-dashed animate-incident-border-pulse bg-transparent';
        }
        return 'border-foreground/30 bg-muted/20 dark:border-foreground/20 dark:bg-muted/10';
    };

    return (
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <CustomTooltip content={(closeTooltip) =>
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                    {getResourceIcon(data.resourceType || '')}
                    {resourceTypeName}
                </div>
                <div className="space-y-1">
                    <div className="text-xs">
                        <span className="font-medium text-muted-foreground">Name:</span>
                        <span className="ml-1">{data.label}</span>
                    </div>
                    {data.privateIp && (
                        <div className="text-xs">
                            <span className="font-medium text-muted-foreground">Private IP:</span>
                            <span className="ml-1 font-mono">{data.privateIp}</span>
                        </div>
                    )}
                    {data.publicIp && (
                        <div className="text-xs">
                            <span className="font-medium text-muted-foreground">Public IP:</span>
                            <span className="ml-1 font-mono">{data.publicIp}</span>
                        </div>
                    )}
                </div>
                {incidentCount > 0 && (
                    <div className="pt-2 border-t">
                        <Badge variant="destructive" className="text-xs">
                            {incidentCount} Active Incident{incidentCount !== 1 ? 's' : ''}
                        </Badge>
                    </div>
                )}
                <Button variant="link" size="sm" className="p-0 h-auto text-xs w-full text-left" onClick={(e) => { e.stopPropagation(); closeTooltip(); setIsDetailsOpen(true); }}>See more details...</Button>
            </div>
        }>
            <div className="relative w-full h-full">
                <Handle type="target" position={Position.Left} style={{ top: '50%' }} />
                <Handle type="source" position={Position.Right} style={{ top: '50%' }} />
                <Card className={`p-2 border-2 transition-all duration-300 hover:shadow-lg w-full h-full cursor-pointer ${getBorderStyles()}`}>
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center">
                            {getResourceIcon(data.resourceType || '')}
                        </div>
                        <div className="mt-1 w-full min-w-0">
                            <h3 className="font-medium text-foreground text-xs break-words line-clamp-2" title={data.label}>{displayName}</h3>
                        </div>
                        <Badge variant="outline" className="text-[9px] mt-0.5 px-1 py-0 truncate max-w-full" title={resourceTypeName}>
                            {resourceTypeName}
                        </Badge>
                    </div>
                </Card>
            </div>
        </CustomTooltip>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                    {getResourceIcon(data.resourceType || '')}
                    AWS {resourceTypeName}: {data.label}
                </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                        <h4 className="font-semibold text-sm border-b pb-1">Basic Information</h4>
                        <div className="space-y-2 text-sm">
                            <div><span className="font-medium text-muted-foreground">Name:</span> <span>{data.label}</span></div>
                            <div><span className="font-medium text-muted-foreground">Type:</span> <span>{resourceTypeName}</span></div>
                            {data.awsRegion && <div><span className="font-medium text-muted-foreground">Region:</span> <span>{data.awsRegion}</span></div>}
                            {data.availabilityZone && <div><span className="font-medium text-muted-foreground">Availability Zone:</span> <span>{data.availabilityZone}</span></div>}
                        </div>
                    </div>
                    <div className="space-y-3">
                        <h4 className="font-semibold text-sm border-b pb-1">Network</h4>
                        <div className="space-y-2 text-sm">
                            {data.privateIp && <div><span className="font-medium text-muted-foreground">Private IP:</span> <span className="font-mono">{data.privateIp}</span></div>}
                            {data.publicIp && <div><span className="font-medium text-muted-foreground">Public IP:</span> <span className="font-mono">{data.publicIp}</span></div>}
                            {!data.privateIp && !data.publicIp && <div className="text-muted-foreground text-xs">No network information available</div>}
                        </div>
                    </div>
                </div>
                <div className="space-y-3">
                    <h4 className="font-semibold text-sm border-b pb-1">AWS Identifiers</h4>
                    <div className="space-y-2 text-sm">
                        {data.arn && (
                            <div>
                                <span className="font-medium text-muted-foreground">ARN:</span>
                                <div className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">{data.arn}</div>
                            </div>
                        )}
                        {data.vpcId && (
                            <div>
                                <span className="font-medium text-muted-foreground">VPC ID:</span>
                                <div className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">{data.vpcId}</div>
                            </div>
                        )}
                        {data.subnetId && (
                            <div>
                                <span className="font-medium text-muted-foreground">Subnet ID:</span>
                                <div className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">{data.subnetId}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </DialogContent>
        </Dialog>
    );
});

// ============================================================================
// OCI CLOUD RESOURCE NODES
// ============================================================================

// Helper to get icon for OCI resource type
// Uses correct OCI type formats: OCI::Core::VCN, OCI::Core::Subnet, etc.
const getOciResourceIcon = (resourceType: string): React.ReactNode => {
    const iconClass = "h-4 w-4 text-muted-foreground";
    switch (resourceType) {
        // Compute
        case 'OCI::Compute::Instance':
            return <Server className={iconClass} />;
        // Core networking (actual types from OCI SDK)
        case 'OCI::Core::VCN':
        case 'OCI::Core::Vcn':
        case 'OCI::Core::Subnet':
        case 'OCI::Core::RouteTable':
            return <Network className={iconClass} />;
        case 'OCI::Core::SecurityList':
        case 'OCI::Core::NetworkSecurityGroup':
            return <Shield className={iconClass} />;
        case 'OCI::Core::InternetGateway':
        case 'OCI::Core::NatGateway':
        case 'OCI::Core::ServiceGateway':
            return <Globe className={iconClass} />;
        // Database
        case 'OCI::Database::AutonomousDatabase':
        case 'OCI::Database::DBSystem':
        case 'OCI::NoSQL::Table':
            return <Database className={iconClass} />;
        // Containers & Kubernetes
        case 'OCI::Container::Cluster':
        case 'OCI::Container::NodePool':
        case 'OCI::ContainerEngine::Cluster':
        case 'OCI::ContainerInstances::ContainerInstance':
            return <Layers className={iconClass} />;
        // Storage
        case 'OCI::ObjectStorage::Bucket':
        case 'OCI::BlockStorage::Volume':
        case 'OCI::BlockStorage::BootVolume':
            return <HardDrive className={iconClass} />;
        // Load Balancer
        case 'OCI::LoadBalancer::LoadBalancer':
            return <Network className={iconClass} />;
        // Identity
        case 'OCI::Identity::User':
            return <User className={iconClass} />;
        case 'OCI::Identity::Group':
            return <Users className={iconClass} />;
        case 'OCI::Identity::Policy':
        case 'OCI::Identity::Compartment':
            return <Shield className={iconClass} />;
        // Serverless
        case 'OCI::Functions::Application':
        case 'OCI::Functions::Function':
            return <Layers className={iconClass} />;
        default:
            return <Cloud className={iconClass} />;
    }
};

// --- OCI TENANCY NODE ---
export const OciTenancyNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const displayName = data.label.length > 25 ? data.label.substring(0, 22) + '...' : data.label;
    const isExpandable = data.isExpandable ?? true;
    const isExpanded = data.isExpanded ?? false;
    const isLoading = data.isLoading ?? false;

    // Truncate OCID for display
    const truncatedOcid = data.ociTenancyOcid
        ? `${data.ociTenancyOcid.substring(0, 20)}...`
        : '';

    return (
        <CustomTooltip content={
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                    <Cloud className="h-5 w-5 text-[#C74634]" />
                    OCI Tenancy
                </div>
                <div className="space-y-1">
                    {data.label && (
                        <div className="text-xs flex items-center gap-2">
                            <span className="font-medium text-muted-foreground">Name:</span>
                            <span>{data.label}</span>
                        </div>
                    )}
                    <div className="text-xs">
                        <span className="font-medium text-muted-foreground">Tenancy OCID:</span>
                        <div className="font-mono text-[10px] break-all mt-0.5">{data.ociTenancyOcid}</div>
                    </div>
                    {data.vcnCount !== undefined && (
                        <div className="text-xs">
                            <span className="font-medium text-muted-foreground">VCNs:</span>
                            <span className="ml-1">{data.vcnCount}</span>
                        </div>
                    )}
                    {data.resourceCount !== undefined && (
                        <div className="text-xs">
                            <span className="font-medium text-muted-foreground">Resources:</span>
                            <span className="ml-1">{data.resourceCount}</span>
                        </div>
                    )}
                </div>
                {isExpandable && (
                    <div className="text-xs text-[#C74634] pt-1 border-t">
                        Click to {isExpanded ? 'collapse' : 'expand'} tenancy
                    </div>
                )}
            </div>
        }>
            <div className="relative w-full h-full">
                <Handle type="target" position={Position.Left} style={{ top: '50%' }} />
                <Handle type="source" position={Position.Right} style={{ top: '50%' }} />
                <Card className={`p-2 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer w-full h-full border-foreground/30 bg-muted/20 dark:border-foreground/20 dark:bg-muted/10 ${isExpandable ? 'hover:border-foreground/40 hover:bg-muted/30 dark:hover:border-foreground/30 dark:hover:bg-muted/20' : ''}`}>
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <div className="relative flex-shrink-0 h-8 w-8 flex items-center justify-center">
                            <Cloud className="h-6 w-6 text-[#C74634]" />
                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/50 rounded">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                </div>
                            )}
                        </div>
                        <div className="mt-1 w-full min-w-0">
                            <h3 className="font-medium text-foreground text-xs break-words line-clamp-2" title={data.label}>{displayName}</h3>
                            <div className="text-[10px] text-muted-foreground font-mono">{truncatedOcid}</div>
                        </div>
                        {isExpandable && (
                            <ExpansionIndicator
                                isExpanded={isExpanded}
                                isLoading={isLoading}
                                childCount={data.vcnCount}
                                itemType="VCNs"
                            />
                        )}
                    </div>
                </Card>
            </div>
        </CustomTooltip>
    );
});

// --- OCI VCN NODE ---
export const OciVcnNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const displayName = data.label.length > 20 ? data.label.substring(0, 17) + '...' : data.label;
    const isExpandable = data.isExpandable ?? true;
    const isExpanded = data.isExpanded ?? false;
    const isLoading = data.isLoading ?? false;
    const isGlobal = data.isGlobalResource ?? false;

    const borderColor = isGlobal ? 'border-muted-foreground/40 dark:border-muted-foreground/30' : 'border-foreground/30 dark:border-foreground/20';
    const bgColor = isGlobal ? 'bg-muted/20 dark:bg-muted/10' : 'bg-muted/20 dark:bg-muted/10';
    const iconColor = isGlobal ? 'text-muted-foreground' : 'text-muted-foreground';

    return (
        <CustomTooltip content={
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                    {isGlobal ? (
                        <Globe className="h-5 w-5 text-gray-500" />
                    ) : (
                        <Network className="h-5 w-5 text-muted-foreground" />
                    )}
                    {isGlobal ? 'Global Resources' : `VCN: ${data.vcnName || data.vcnId}`}
                </div>
                <div className="space-y-1">
                    {!isGlobal && (
                        <>
                            <div className="text-xs">
                                <span className="font-medium text-muted-foreground">VCN OCID:</span>
                                <div className="font-mono text-[10px] break-all mt-0.5">{data.vcnId}</div>
                            </div>
                            {data.ociRegion && (
                                <div className="text-xs">
                                    <span className="font-medium text-muted-foreground">Region:</span>
                                    <span className="ml-1">{data.ociRegion}</span>
                                </div>
                            )}
                        </>
                    )}
                    {data.resourceCount !== undefined && (
                        <div className="text-xs">
                            <span className="font-medium text-muted-foreground">Resources:</span>
                            <span className="ml-1">{data.resourceCount}</span>
                        </div>
                    )}
                </div>
                {isExpandable && (
                    <div className={`text-xs pt-1 border-t ${isGlobal ? 'text-gray-600 dark:text-gray-400' : 'text-muted-foreground'}`}>
                        Click to {isExpanded ? 'collapse' : 'expand'}
                    </div>
                )}
            </div>
        }>
            <div className="relative w-full h-full">
                <Handle type="target" position={Position.Left} style={{ top: '50%' }} />
                <Handle type="source" position={Position.Right} style={{ top: '50%' }} />
                <Card className={`p-2 border-2 transition-all duration-300 hover:shadow-lg cursor-pointer w-full h-full ${borderColor} ${bgColor}`}>
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <div className="relative flex-shrink-0 h-7 w-7 flex items-center justify-center">
                            {isGlobal ? (
                                <Globe className={`h-5 w-5 ${iconColor}`} />
                            ) : (
                                <Network className={`h-5 w-5 ${iconColor}`} />
                            )}
                            {isLoading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/50 rounded">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                </div>
                            )}
                        </div>
                        <div className="mt-1 w-full min-w-0">
                            <h3 className="font-medium text-foreground text-xs break-words line-clamp-2" title={data.label}>{displayName}</h3>
                            {!isGlobal && data.ociRegion && (
                                <div className="text-[10px] text-muted-foreground">{data.ociRegion}</div>
                            )}
                        </div>
                        {isExpandable && (
                            <ExpansionIndicator
                                isExpanded={isExpanded}
                                isLoading={isLoading}
                                childCount={data.resourceCount}
                                itemType="resources"
                            />
                        )}
                    </div>
                </Card>
            </div>
        </CustomTooltip>
    );
});

// --- OCI GLOBAL GROUP NODE (for Identity, Object Storage) ---
export const OciGlobalGroupNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    // Re-use OciVcnNode with isGlobalResource flag
    return <OciVcnNode data={{ ...data, isGlobalResource: true }} />;
});

// --- OCI RESOURCE NODE ---
export const OciResourceNode: React.FC<{ data: CustomNodeData }> = memo(({ data }) => {
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);
    const displayName = data.label.length > 18 ? data.label.substring(0, 15) + '...' : data.label;
    const resourceTypeName = getOciResourceTypeName(data.resourceType || '');
    const incidentCount = data.incidentCount ?? 0;

    // Determine border style - red pulse if incidents exist, default red otherwise
    const getBorderStyles = () => {
        if (incidentCount > 0) {
            return 'border-2 border-dashed animate-incident-border-pulse bg-transparent';
        }
        return 'border-foreground/30 bg-muted/20 dark:border-foreground/20 dark:bg-muted/10';
    };

    return (
        <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <CustomTooltip content={(closeTooltip) =>
            <div className="space-y-2">
                <div className="font-semibold text-foreground flex items-center gap-2 text-sm">
                    {getOciResourceIcon(data.resourceType || '')}
                    {resourceTypeName}
                </div>
                <div className="space-y-1">
                    <div className="text-xs">
                        <span className="font-medium text-muted-foreground">Name:</span>
                        <span className="ml-1">{data.label}</span>
                    </div>
                    {data.privateIp && (
                        <div className="text-xs">
                            <span className="font-medium text-muted-foreground">Private IP:</span>
                            <span className="ml-1 font-mono">{data.privateIp}</span>
                        </div>
                    )}
                    {data.publicIp && (
                        <div className="text-xs">
                            <span className="font-medium text-muted-foreground">Public IP:</span>
                            <span className="ml-1 font-mono">{data.publicIp}</span>
                        </div>
                    )}
                </div>
                {incidentCount > 0 && (
                    <div className="pt-2 border-t">
                        <Badge variant="destructive" className="text-xs">
                            {incidentCount} Active Incident{incidentCount !== 1 ? 's' : ''}
                        </Badge>
                    </div>
                )}
                <Button variant="link" size="sm" className="p-0 h-auto text-xs w-full text-left" onClick={(e) => { e.stopPropagation(); closeTooltip(); setIsDetailsOpen(true); }}>See more details...</Button>
            </div>
        }>
            <div className="relative w-full h-full">
                <Handle type="target" position={Position.Left} style={{ top: '50%' }} />
                <Handle type="source" position={Position.Right} style={{ top: '50%' }} />
                <Card className={`p-2 border-2 transition-all duration-300 hover:shadow-lg w-full h-full cursor-pointer ${getBorderStyles()}`}>
                    <div className="flex flex-col items-center justify-center h-full text-center overflow-hidden">
                        <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center">
                            {getOciResourceIcon(data.resourceType || '')}
                        </div>
                        <div className="mt-1 w-full min-w-0">
                            <h3 className="font-medium text-foreground text-xs break-words line-clamp-2" title={data.label}>{displayName}</h3>
                        </div>
                        <Badge variant="outline" className="text-[9px] mt-0.5 px-1 py-0 truncate max-w-full" title={resourceTypeName}>
                            {resourceTypeName}
                        </Badge>
                    </div>
                </Card>
            </div>
        </CustomTooltip>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                    {getOciResourceIcon(data.resourceType || '')}
                    OCI {resourceTypeName}: {data.label}
                </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                        <h4 className="font-semibold text-sm border-b pb-1">Basic Information</h4>
                        <div className="space-y-2 text-sm">
                            <div><span className="font-medium text-muted-foreground">Name:</span> <span>{data.label}</span></div>
                            <div><span className="font-medium text-muted-foreground">Type:</span> <span>{resourceTypeName}</span></div>
                            {data.ociRegion && <div><span className="font-medium text-muted-foreground">Region:</span> <span>{data.ociRegion}</span></div>}
                            {data.availabilityDomain && <div><span className="font-medium text-muted-foreground">Availability Domain:</span> <span>{data.availabilityDomain}</span></div>}
                        </div>
                    </div>
                    <div className="space-y-3">
                        <h4 className="font-semibold text-sm border-b pb-1">Network</h4>
                        <div className="space-y-2 text-sm">
                            {data.privateIp && <div><span className="font-medium text-muted-foreground">Private IP:</span> <span className="font-mono">{data.privateIp}</span></div>}
                            {data.publicIp && <div><span className="font-medium text-muted-foreground">Public IP:</span> <span className="font-mono">{data.publicIp}</span></div>}
                            {!data.privateIp && !data.publicIp && <div className="text-muted-foreground text-xs">No network information available</div>}
                        </div>
                    </div>
                </div>
                <div className="space-y-3">
                    <h4 className="font-semibold text-sm border-b pb-1">OCI Identifiers</h4>
                    <div className="space-y-2 text-sm">
                        {data.ocid && (
                            <div>
                                <span className="font-medium text-muted-foreground">OCID:</span>
                                <div className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">{data.ocid}</div>
                            </div>
                        )}
                        {data.vcnId && data.vcnId !== 'global' && (
                            <div>
                                <span className="font-medium text-muted-foreground">VCN ID:</span>
                                <div className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">{data.vcnId}</div>
                            </div>
                        )}
                        {data.subnetId && (
                            <div>
                                <span className="font-medium text-muted-foreground">Subnet ID:</span>
                                <div className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">{data.subnetId}</div>
                            </div>
                        )}
                        {data.compartmentId && (
                            <div>
                                <span className="font-medium text-muted-foreground">Compartment ID:</span>
                                <div className="font-mono text-xs bg-muted p-2 rounded mt-1 break-all">{data.compartmentId}</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </DialogContent>
        </Dialog>
    );
});
